import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faBan, faCreditCard, faSearch } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import { amenityCommonApi } from '../amenityCommonApi';

const AmenityBookingsList = ({ siteId, authUser }) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    
    // Payment Modal State
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [paymentData, setPaymentData] = useState({
        paidAmount: '',
        paymentStatus: 'Paid'
    });

    useEffect(() => {
        fetchBookings();
    }, [siteId]);

    const fetchBookings = async () => {
        if (!siteId) return;
        setLoading(true);
        try {
            const res = await amenityCommonApi.getBookings({ siteId });
            if ((res.data?.isSuccess || res.data?.IsSuccess) && Array.isArray(res.data.data)) {
                const mapped = res.data.data.map(b => ({
                    bookingId: b.BookingId || b.bookingId,
                    amenityId: b.AmenityId || b.amenityId,
                    amenityName: b.AmenityName || b.amenityName,
                    unitId: b.UnitId || b.unitId,
                    unitName: b.UnitName || b.unitName,
                    unitNumber: b.UnitNumber || b.unitNumber,
                    siteId: b.SiteId || b.siteId,
                    userId: b.UserId || b.userId,
                    userName: b.UserName || b.userName,
                    guestCount: b.GuestCount || b.guestCount,
                    bookingDate: b.BookingDate || b.bookingDate,
                    startTime: b.StartTime || b.startTime,
                    endTime: b.EndTime || b.endTime,
                    bookingStatus: b.BookingStatus || b.bookingStatus,
                    paidAmount: b.PaidAmount || b.paidAmount,
                    payableAmount: b.PayableAmount || b.payableAmount,
                    paymentMethod: b.PaymentMethod || b.paymentMethod,
                    paymentStatus: b.PaymentStatus || b.paymentStatus,
                    isActiveId: b.IsActiveId || b.isActiveId
                }));
                // sort descending by ID
                setBookings(mapped.sort((a, b) => b.bookingId - a.bookingId));
            } else {
                setBookings([]);
            }
        } catch (error) {
            toast.error('Failed to fetch bookings.');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (bookingId, actionStatus) => {
        const actionStr = actionStatus.toLowerCase();
        if (!window.confirm(`Are you sure you want to ${actionStr} this booking?`)) return;

        try {
            let res;
            if (actionStatus === 'Cancelled') {
                res = await amenityCommonApi.cancelBooking(bookingId);
            } else {
                // 'Approved' or 'Rejected'
                res = await amenityCommonApi.approveOrRejectBooking(bookingId, actionStatus);
            }

            if (res.data?.isSuccess || res.data?.IsSuccess) {
                toast.success(res.data.message || res.data.Message || `Booking ${actionStr} successfully.`);
                // Optimistic update
                setBookings(prev => prev.map(b => b.bookingId === bookingId ? { ...b, bookingStatus: actionStatus } : b));
            } else {
                toast.error(res.data?.message || res.data?.Message || `Failed to ${actionStr} booking.`);
            }
        } catch (error) {
            toast.error(`Error trying to ${actionStr} booking.`);
        }
    };

    const openPaymentModal = (booking) => {
        setSelectedBooking(booking);
        setPaymentData({
            paidAmount: booking.paidAmount || '',
            paymentStatus: 'Paid'
        });
        setPaymentModalOpen(true);
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        
        if (!paymentData.paidAmount || isNaN(paymentData.paidAmount)) {
            toast.error("Please enter a valid amount.");
            return;
        }

        const payload = {
            bookingId: selectedBooking.bookingId,
            paidAmount: Number(paymentData.paidAmount),
            paymentMethod: "Manual",
            paymentStatus: paymentData.paymentStatus
        };

        try {
            const res = await amenityCommonApi.updatePayment(payload);
            if (res.data?.isSuccess || res.data?.IsSuccess) {
                toast.success(res.data.message || res.data.Message || "Payment recorded successfully.");
                setPaymentModalOpen(false);
                // Optimistic update
                setBookings(prev => prev.map(b => b.bookingId === selectedBooking.bookingId ? 
                    { ...b, paidAmount: payload.paidAmount, paymentStatus: payload.paymentStatus, paymentMethod: "Manual" } : b
                ));
            } else {
                toast.error(res.data?.message || res.data?.Message || "Failed to record payment.");
            }
        } catch (error) {
            toast.error("Error updating payment.");
        }
    };

    const filteredBookings = useMemo(() => {
        let data = [...bookings];

        // Search Filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            data = data.filter(b => 
                (b.amenityName && b.amenityName.toLowerCase().includes(term)) ||
                (b.userName && b.userName.toLowerCase().includes(term))
            );
        }

        // Status Filter
        if (statusFilter !== 'All') {
            data = data.filter(b => b.bookingStatus === statusFilter);
        }

        return data;
    }, [bookings, searchTerm, statusFilter]);

    const tableHeaders = [
        { label: "SR NO", key: "sno", align: "center" },
        { label: "AMENITY", key: "amenityName", align: "left" },
        { label: "BOOKED BY", key: "userName", align: "left" },
        { label: "DATE", key: "bookingDate", align: "center" },
        { label: "TIME SLOT", key: "time", align: "center" },
        { label: "GUESTS", key: "guestCount", align: "center" },
        { label: "PAID AMT", key: "paidAmount", align: "center" },
        { label: "PAY STATUS", key: "paymentStatus", align: "center" },
        { label: "STATUS", key: "bookingStatus", align: "center" },
        { label: "ACTIONS", key: "actionButtons", align: "center" },
    ];

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Pending': return 'bg-[#FEF9C3] text-[#D97706]';
            case 'Approved': return 'bg-[#DCFCE7] text-[#166534]';
            case 'Rejected': return 'bg-[#E31F26] text-white';
            case 'Cancelled': return 'bg-gray-200 text-gray-600';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getPaymentStatusStyle = (status) => {
        switch (status) {
            case 'Paid': return 'text-green-600 font-bold';
            case 'Partial': return 'text-yellow-600 font-bold';
            case 'Unpaid': return 'text-red-500 font-bold';
            default: return 'text-gray-500 font-medium';
        }
    };

    const renderCell = (key, row, header) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{bookings.indexOf(row) + 1}</span>;
            case "amenityName":
                return <span className="font-bold text-gray-800 tracking-tight">{row.amenityName}</span>;
            case "userName":
                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700">{row.userName}</span>
                        {(row.unitName || row.unitNumber) && <span className="text-[10px] text-gray-400">Unit: {row.unitName || ''} {row.unitNumber ? `(${row.unitNumber})` : ''}</span>}
                        {(!row.unitName && !row.unitNumber && row.unitId) && <span className="text-[10px] text-gray-400">Unit ID: {row.unitId}</span>}
                    </div>
                );
            case "bookingDate":
                return <span className="text-xs font-medium text-gray-600">{row.bookingDate?.split('T')[0]}</span>;
            case "time":
                return <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{row.startTime?.substring(0,5)} - {row.endTime?.substring(0,5)}</span>;
            case "guestCount":
                return <span className="text-xs font-semibold">{row.guestCount}</span>;
            case "paidAmount":
                return <span className="text-xs font-bold text-gray-700">{row.paidAmount ? `₹${row.paidAmount}` : '-'}</span>;
            case "paymentStatus":
                return <span className={`text-[11px] uppercase ${getPaymentStatusStyle(row.paymentStatus)}`}>{row.paymentStatus || 'UNPAID'}</span>;
            case "bookingStatus":
                return (
                    <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase shadow-sm whitespace-nowrap ${getStatusStyle(row.bookingStatus)}`}>
                        {row.bookingStatus}
                    </span>
                );
            case "actionButtons":
                if (row.bookingStatus === 'Pending') {
                    return (
                        <div className="flex gap-2 justify-center items-center">
                            <button onClick={() => handleAction(row.bookingId, 'Approved')} className="text-green-600 hover:text-green-800 hover:bg-green-50 p-1.5 rounded transition-colors" title="Approve">
                                <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button onClick={() => handleAction(row.bookingId, 'Rejected')} className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded transition-colors" title="Reject">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                            <button onClick={() => handleAction(row.bookingId, 'Cancelled')} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded transition-colors" title="Cancel">
                                <FontAwesomeIcon icon={faBan} />
                            </button>
                        </div>
                    );
                }
                if (row.bookingStatus === 'Approved') {
                    return (
                        <button 
                            onClick={() => openPaymentModal(row)}
                            className="bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 px-2 py-1 rounded text-[10px] font-bold tracking-wider transition-colors flex items-center gap-1 mx-auto"
                        >
                            <FontAwesomeIcon icon={faCreditCard} />
                            PAY
                        </button>
                    );
                }
                return <span className="text-gray-400">-</span>;
            default:
                return row[key] || "-";
        }
    };

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:w-64">
                <input
                    type="text"
                    placeholder="Search amenity or user..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            </div>
            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
                <option value="All">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Cancelled">Cancelled</option>
            </select>
        </div>
    );

    return (
        <div className="flex flex-col gap-6 relative">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">Booking Management</h3>
                    <p className="text-sm text-gray-500">Review, approve, and manage amenity bookings and payments.</p>
                </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <CommonTable
                    loading={loading}
                    tableHeading="Bookings List"
                    headers={tableHeaders}
                    data={filteredBookings}
                    renderCell={renderCell}
                    headerAction={headerAction}
                    pagination={null} // Can add pagination component here
                />
            </div>

            {/* Payment Modal */}
            {paymentModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-bold text-gray-800">Record Payment</h3>
                            <button onClick={() => setPaymentModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <form onSubmit={handlePaymentSubmit} className="p-6 flex flex-col gap-5">
                            <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-sm mb-2">
                                <p><span className="font-semibold">Booking:</span> {selectedBooking?.amenityName}</p>
                                <p><span className="font-semibold">User:</span> {selectedBooking?.userName}</p>
                                <p className="mt-1 border-t border-blue-200 pt-1">
                                    <span className="font-bold text-red-600">Payable Amount:</span> <span className="font-bold">₹{selectedBooking?.payableAmount ?? '0'}</span>
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Amount Collected (₹) *</label>
                                <input
                                    type="number"
                                    value={paymentData.paidAmount}
                                    onChange={(e) => setPaymentData({...paymentData, paidAmount: e.target.value})}
                                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter amount"
                                    required
                                    min="0"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Method</label>
                                <input
                                    type="text"
                                    value="Manual Collection"
                                    className="w-full border border-gray-200 bg-gray-100 rounded-lg p-2.5 text-sm text-gray-500 cursor-not-allowed"
                                    disabled
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Status *</label>
                                <select
                                    value={paymentData.paymentStatus}
                                    onChange={(e) => setPaymentData({...paymentData, paymentStatus: e.target.value})}
                                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="Paid">Fully Paid</option>
                                    <option value="Partial">Partially Paid</option>
                                    <option value="Unpaid">Unpaid</option>
                                </select>
                            </div>

                            <div className="mt-4 flex gap-3 justify-end">
                                <button type="button" onClick={() => setPaymentModalOpen(false)} className="px-5 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" className="px-6 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm">
                                    Save Payment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AmenityBookingsList;
